import React, { useCallback } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

export interface FieldSimulationProps {
  size: number;
  color: string;
  lineCount: number;
  direction: 'radial' | 'dipole';
}

export function FieldSimulation({ size, color, lineCount, direction }: FieldSimulationProps) {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, frameCount: number) => {
      ctx.clearRect(0, 0, size, size);
      
      const center = size / 2;
      const coreRadius = size * 0.1;
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      
      // Animate flowing lines
      ctx.setLineDash([10, 10]);
      ctx.lineDashOffset = -frameCount * 0.5;

      if (direction === 'radial') {
        // Lines expanding outward
        for (let i = 0; i < lineCount; i++) {
          const angle = (Math.PI * 2 * i) / lineCount;
          ctx.beginPath();
          ctx.moveTo(
            center + Math.cos(angle) * coreRadius,
            center + Math.sin(angle) * coreRadius
          );
          ctx.lineTo(
            center + Math.cos(angle) * size,
            center + Math.sin(angle) * size
          );
          ctx.stroke();
        }
      } else if (direction === 'dipole') {
        // Lines looping from North to South pole
        // We draw arcs that start at top pole, bow outward, and end at bottom pole
        const poles = {
          n: { x: center, y: center - coreRadius },
          s: { x: center, y: center + coreRadius }
        };

        const halfCount = Math.floor(lineCount / 2);
        
        for (let side of [-1, 1]) {
          for (let i = 1; i <= halfCount; i++) {
            const bowOut = (size * 0.8 / halfCount) * i * side;
            
            ctx.beginPath();
            ctx.moveTo(poles.n.x, poles.n.y);
            // Quadratic or bezier curve
            ctx.bezierCurveTo(
              center + bowOut, poles.n.y - coreRadius,
              center + bowOut, poles.s.y + coreRadius,
              poles.s.x, poles.s.y
            );
            ctx.stroke();
          }
        }
      }

      ctx.setLineDash([]); // Reset
      
      // Draw Central Core
      ctx.beginPath();
      ctx.arc(center, center, coreRadius, 0, Math.PI * 2);
      ctx.fillStyle = '#1e293b';
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    },
    [size, color, lineCount, direction]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
